/**
 * What this account has decided about its documents.
 *
 * ONE SETTING TODAY, and the page exists anyway: "must every comment be answered before a
 * procedure can be released?" is a quality-system policy, quality systems differ, and the answer
 * belongs to the organisation rather than to us. A constant in the code would have been one
 * organisation's answer imposed on all of them.
 *
 * GATED ON A PRIVILEGE, NOT ON BEING THE ADMINISTRATOR. The Administrator is an ordinary role
 * holding every privilege by construction -- a bypass would make Effective privileges and
 * Granted-by lie -- so this asks for `document:configure`. The rail hides the icon without it and
 * the service refuses the write without it; the second is the one that counts, because a control
 * the client hides is a control a request can still reach.
 *
 * IT SAYS WHAT IS NOT CONFIGURABLE, AND WHY. Unresolved tracked changes always stop a submission,
 * and that is not an oversight: the content digest is taken over the raw body, so a body still
 * carrying insertions and deletions would make a signature cover two versions of the text at once.
 * A settings page that silently omitted it would read as though nobody had thought about it.
 */
import React from "react";
import {
    Alert, Box, CircularProgress, FormControlLabel, Switch, Typography,
} from "@mui/material";

import useAppContext from "../util/AppContext";
import { makeCresAxios } from "../util/CresAxios";
import { Blue4, Grey5, Grey6, Grey17 } from "../theme/styles";

type Settings = {
    BlockReleaseOnComments: boolean;
    LastModifiedBy: string | null;
    LastModifiedAt: string | null;
    MayConfigure: boolean;
};

export default function SettingsPage() {
    const sessionContext = useAppContext()?.sessionContext as any;
    const axios = React.useMemo(() => makeCresAxios(sessionContext), [sessionContext]);

    const [settings, setSettings] = React.useState<Settings | null>(null);
    const [problem, setProblem] = React.useState<string | null>(null);
    const [saved, setSaved] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);

    React.useEffect(() => {
        (async () => {
            try {
                const answer = await axios.get("/t11/v1/document_setting");
                setSettings(answer?.data ?? null);
            } catch (error: any) {
                setProblem(error?.response?.data?.Cause || "The settings could not be loaded.");
            }
        })();
    }, [axios]);

    const change = async (next: Partial<Settings>) => {
        if (!settings) return;
        setBusy(true);
        setSaved(null);
        try {
            const answer = await axios.post("/t11/v1/document_setting", {
                BlockReleaseOnComments: next.BlockReleaseOnComments
                    ?? settings.BlockReleaseOnComments,
            });
            setSettings(answer?.data ?? settings);
            setProblem(null);
            setSaved("Saved.");
        } catch (error: any) {
            // THE SERVER'S OWN SENTENCE. It names the privilege and says who can grant it, which is
            // more use than anything this page could invent.
            setProblem(error?.response?.data?.Cause || "That change could not be saved.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between",
                         height: 52, backgroundColor: "#1498D8"}}>
                <div style={{paddingTop: "9px", paddingLeft: "20px"}}>
                    <Typography style={{fontSize: 24, fontWeight: 400, color: "#FFFFFF"}}>
                        Settings
                    </Typography>
                </div>
            </div>

            <Box sx={{ p: 5, maxWidth: "820px" }}>
                {problem && (
                    <Alert severity="warning" sx={{ mb: 4 }} onClose={() => setProblem(null)}>
                        {problem}
                    </Alert>
                )}
                {saved && (
                    <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSaved(null)}>
                        {saved}
                    </Alert>
                )}

                {!settings ? (
                    <CircularProgress size={22}/>
                ) : (
                    <>
                        <Typography sx={{ fontSize: 17, fontWeight: 600, color: Blue4 }}>
                            Sending a revision for approval
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, color: Grey6, mt: 2, lineHeight: 1.6 }}>
                            What has to be settled in a document before it can go to approval.
                        </Typography>

                        <Box sx={{ mt: 5 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={settings.BlockReleaseOnComments}
                                        disabled={!settings.MayConfigure || busy}
                                        onChange={(_, value) =>
                                            void change({ BlockReleaseOnComments: value })}/>
                                }
                                label={
                                    <Typography sx={{ fontSize: 14, color: Grey17 }}>
                                        Unresolved comments stop a revision being sent for approval
                                    </Typography>
                                }/>
                            <Typography sx={{ fontSize: 13, color: Grey6, ml: 6, lineHeight: 1.6 }}>
                                A comment is a question rather than a change, so this is off unless
                                you turn it on. With it on, every thread has to be resolved — not
                                answered, resolved — before a revision can be submitted.
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 6, pt: 4, borderTop: "1px solid #EDEFF3" }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: Grey17 }}>
                                Unresolved tracked changes always stop a submission
                            </Typography>
                            {/* SAID OUT LOUD RATHER THAN OMITTED. A settings page that showed one
                                of these two rules and not the other would read as though nobody
                                had thought about the second. */}
                            <Typography sx={{ fontSize: 13, color: Grey6, mt: 1.5, lineHeight: 1.6 }}>
                                This one is not configurable, and the reason is worth knowing: what
                                an approval covers is a hash of the document's text, and a document
                                still carrying insertions and deletions is two versions of that text
                                at once. A signature against it would not record which one was
                                approved. Comments are different — their anchors are removed before
                                the hash is taken, which is why they can be a policy rather than a
                                rule.
                            </Typography>
                        </Box>

                        {!settings.MayConfigure && (
                            <Typography sx={{ fontSize: 12.5, color: Grey5, mt: 6 }}>
                                You can see this page but not change it. Changing document settings
                                needs the Document: Configure privilege.
                            </Typography>
                        )}
                        {settings.LastModifiedBy && (
                            <Typography sx={{ fontSize: 12.5, color: Grey5, mt: 4 }}>
                                Last changed by {settings.LastModifiedBy}
                                {settings.LastModifiedAt
                                    ? ` on ${new Date(settings.LastModifiedAt).toLocaleString()}` : ""}.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </div>
    );
}
